import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import {
    createFieldInput,
    createFieldInputNumber,
    createFieldRadioGroup,
    createFieldSelect
} from "../../../../../components/input/template-builder/template-builder.logic";
import { createColumnInlineRadioGroup, createColumnInput } from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, putField } from "../../../../../utils/form";
import { createPage } from "../../../../../utils/page";

const Address = defineAsyncComponent(() => import("../../../../../components/address/Address"));
const CTable = defineAsyncComponent(() => import("../../../../../components/table/CTable"));
const StorageLocationExpertList = defineAsyncComponent(() => import("./StorageLocationExpertList"));

const yesNo = [
    { id: "1", name: i18n.global.t("button.yes") },
    { id: "2", name: i18n.global.t("button.no") }
];
const qnaStoragePage = createPage({
    data: [
        { name: i18n.global.t("views.request.storageLocation.isSuitablePosition"), key: "isSuitablePosition" },
        { name: i18n.global.t("views.request.storageLocation.isRoyalPalace"), key: "isRoyalPalace" },
        { name: i18n.global.t("views.request.storageLocation.isHome"), key: "isHome" },
        { name: i18n.global.t("views.request.storageLocation.isShoppingArea"), key: "isShoppingArea" },
        { name: i18n.global.t("views.request.storageLocation.isPublicPlace"), key: "isPublicPlace" },
        { name: i18n.global.t("views.request.storageLocation.isIndustrialArea"), key: "isIndustrialArea" }
    ]
});
const qnaManufacturePage = createPage({
    data: [
        { name: i18n.global.t("views.request.storageLocation.isSuitablePosition"), key: "isSuitablePosition" },
        { name: i18n.global.t("views.request.storageLocation.isRoyalPalace"), key: "isRoyalPalace" },
        { name: i18n.global.t("views.request.storageLocation.isHome"), key: "isHome" },
        { name: i18n.global.t("views.request.storageLocation.isShoppingArea"), key: "isShoppingArea" },
        { name: i18n.global.t("views.request.storageLocation.isPublicPlace"), key: "isPublicPlace" },
        { name: i18n.global.t("views.request.storageLocation.isIndustrialArea"), key: "isIndustrialArea" },
        { name: i18n.global.t("views.request.storageLocation.isSafety"), key: "isSafety" },
        { name: i18n.global.t("views.request.storageLocation.isWaste"), key: "isWaste" },
        { name: i18n.global.t("views.request.storageLocation.isChemical"), key: "isChemical" }
    ]
});

export const callOwnershipType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "OWNERSHIP_TYPE", value: searchQuery });
export const callLocationType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LOCATION_TYPE", value: searchQuery });
export const callLocationProductType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LOCATION_PRODUCT_TYPE", value: searchQuery });

export const createField = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext, isLocation } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";

    const qnaColumns = [
        createColumnInput({
            title: () => isLocation ? i18n.global.t("views.request.storageLocation.desc") : i18n.global.t("views.request.manufactureLocation.desc2"),
            dataIndex: "name",
            width: 450
        }),
        createColumnInlineRadioGroup({
            useForm,
            key: ({ record }) => `${prefix}${record.key}`,
            inputProp: () => ({ list: () => yesNo }),
            customRenderInputProp: ({ record }) =>
                lodash.merge(
                    {
                        list: () => yesNo,
                        value:
                            model[record.key] === true
                                ? yesNo.find(d => d.id === "1")
                                : model[record.key] === false
                                ? yesNo.find(d => d.id === "2")
                                : undefined,
                        "onUpdate:value": value => (model[record.key] = value.id === "1" ? true : value.id === "2" ? false : undefined),
                        disabled: viewOnly
                    },
                    conditionFormChecker({
                        props,
                        data,
                        executeKey: "inputPropField",
                        params: { field: { key: `${prefix}${record.key}` } }
                    })
                ),
            sorter: false,
            align: "center",
            width: 150
        })
    ];

    return putField({
        data,
        fields: [
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}name`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => (isLocation ? i18n.global.t("model.storageLocation.name") : i18n.global.t("views.request.manufactureLocation.name")),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.name,
                            "onUpdate:value": value => (model.name = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}idTax`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.idTax"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.idTax,
                            "onUpdate:value": value => (model.idTax = value),
                            disabled: viewOnly,
                            comma: false,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}email`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.email"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.email,
                            "onUpdate:value": value => (model.email = value),
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => (
                    <Address
                        menuContext={menuContext}
                        viewOnly={viewOnly}
                        dataProp={data}
                        dataKey={`${prefix}addresses.0`}
                        isLoading={isLoading.value}
                    ></Address>
                )
            },
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refOwnershipType`,
                colProp: () => ({ xs: 24, class: "mt-5" }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.refOwnershipType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refOwnershipType,
                            "onUpdate:value": value => (model.refOwnershipType = value),
                            disabled: viewOnly,
                            list: callOwnershipType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    return (
                        <CTable
                            isLoading={isLoading.value}
                            columns={qnaColumns}
                            page={isLocation ? qnaStoragePage : qnaManufacturePage}
                            clientSide={true}
                            canRefresh={false}
                            visibleSearch={false}
                            visiblePagination={false}
                            visibleRowActions={false}
                        ></CTable>
                    );
                }
            },
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}types`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 }, labelAlign: "left" }),
                label: () => isLocation ? i18n.global.t("views.request.storageLocation.types") : i18n.global.t("views.request.storageLocation.manuTypes"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.types,
                            "onUpdate:value": value => (model.types = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callLocationType({ searchQuery }).then(items => items.map(item => ({ refLocationType: item }))),
                            optLabel: "refLocationType.value",
                            mode: "multiple",
                            optKey: "refLocationType.id"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValueChildren: ({ record }) => record.refLocationType?.value }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}productTypes`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 }, labelAlign: "left" }),
                label: () => isLocation ? i18n.global.t("views.request.storageLocation.productTypes") : i18n.global.t("views.request.storageLocation.manuProductTypes"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.productTypes,
                            "onUpdate:value": value => (model.productTypes = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callLocationProductType({ searchQuery }).then(items => items.map(item => ({ refLocationProductType: item }))),
                            optLabel: "refLocationProductType.value",
                            mode: "multiple",
                            optKey: "refLocationProductType.id"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValueChildren: ({ record }) => record.refLocationProductType?.value }
            }),
            {
                visible: () => true,
                key: `${prefix}experts`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 }, labelAlign: "left" }),
                label: () => i18n.global.t("views.request.storageLocation.experts"),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}experts`,
                            fetchCriteria: data.expert?.fetchCriteria,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "StorageLocationExpertList" } })
                    );
                    return <StorageLocationExpertList {...componentProp}></StorageLocationExpertList>;
                }
            },
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}productionArea`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.productionArea"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.productionArea,
                            "onUpdate:value": value => (model.productionArea = value),
                            disabled: viewOnly,
                            allowPrecision: true,
                            addonAfter: () => <div>/ {i18n.global.t('views.request.manufactureLocation.squareMeter')}</div>
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}horsepower`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.horsepower"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.horsepower,
                            "onUpdate:value": value => (model.horsepower = value),
                            disabled: viewOnly,
                            allowPrecision: true,
                            addonAfter: () => <div>/ {i18n.global.t('views.request.manufactureLocation.horsepower')}</div>
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}worker`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.worker"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.worker,
                            "onUpdate:value": value => (model.worker = value),
                            disabled: viewOnly,
                            addonAfter: () => <div>/ {i18n.global.t('views.request.manufactureLocation.worker')}</div>
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            })
        ]
    });
};
